<script lang="ts">
  import type { IChatKitWidgetInstance } from '../interfaces/chatkit-widget.interface';
  import ChatKitWidgetNode from './chatkit-widget-node.svelte';

  export let widgets: IChatKitWidgetInstance[] = [];
</script>

{#if widgets?.length}
  <div
    class="cs-chatkit-widgets"
    data-chatkit-tool-call-ids={widgets
      .map((widget) => widget.toolCallId)
      .filter(Boolean)
      .join(',')}
    data-chatkit-question-ids={widgets
      .map((widget) => widget.questionId)
      .filter(Boolean)
      .join(',')}
  >
    {#each widgets as widget (widget.id)}
      <ChatKitWidgetNode node={widget.template} {widget} />
    {/each}
  </div>
{/if}

<style lang="scss">
  .cs-chatkit-widgets {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 12px 0;
    max-width: min(100%, 520px);
  }

  :global(.cs-chatkit-card) {
    background: #fff;
    border: 1px solid rgba(15, 23, 42, 0.12);
    border-radius: 12px;
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.1);
    box-sizing: border-box;
    color: #172033;
    overflow: hidden;
    padding: 16px;
  }

  :global(.cs-chatkit-size-sm) {
    max-width: 320px;
  }

  :global(.cs-chatkit-size-md) {
    max-width: 440px;
  }

  :global(.cs-chatkit-size-lg) {
    max-width: 560px;
  }

  :global(.cs-chatkit-size-full) {
    max-width: 100%;
    width: 100%;
  }

  :global(.cs-chatkit-box),
  :global(.cs-chatkit-form) {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  :global(.cs-chatkit-row) {
    align-items: center;
    flex-direction: row;
    min-width: 0;
    width: 100%;
  }

  :global(.cs-chatkit-list) {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
  }

  :global(.cs-chatkit-list-horizontal) {
    box-sizing: border-box;
    flex-direction: row;
    max-width: 100%;
    min-width: 0;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-inline: contain;
    padding: 4px 2px 12px;
    scroll-padding-inline: 2px;
    scroll-snap-type: inline proximity;
    scrollbar-width: thin;
  }

  :global(.cs-chatkit-list-horizontal > *) {
    flex: 0 0 auto;
    scroll-snap-align: start;
  }

  :global(.cs-chatkit-list-item) {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: 8px;
    color: inherit;
    display: flex;
    gap: 12px;
    min-width: 0;
    padding: 10px 12px;
    text-align: left;
    width: 100%;
  }

  :global(.cs-chatkit-list-item > .cs-chatkit-image) {
    flex-shrink: 0;
  }

  :global(.cs-chatkit-list-item > .cs-chatkit-col) {
    flex: 1 1 0;
    min-width: 0;
  }

  :global(.cs-chatkit-list-item > .cs-chatkit-spacer) {
    flex: 0 0 8px;
    min-width: 8px;
  }

  :global(.cs-chatkit-list-item > .cs-chatkit-button),
  :global(.cs-chatkit-list-item > .cs-chatkit-icon) {
    flex: 0 0 auto;
  }

  :global(.cs-chatkit-clickable) {
    cursor: pointer;
  }

  :global(.cs-chatkit-clickable:hover) {
    background: rgba(15, 23, 42, 0.06);
  }

  :global(.cs-chatkit-clickable:focus-visible) {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }

  :global(.cs-chatkit-card.cs-chatkit-clickable) {
    transition:
      border-color 120ms ease,
      box-shadow 120ms ease,
      transform 120ms ease;
  }

  :global(.cs-chatkit-card.cs-chatkit-clickable:hover) {
    background: #fff;
    border-color: rgba(15, 23, 42, 0.24);
    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.14);
    transform: translateY(-1px);
  }

  :global(.cs-chatkit-actions) {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    margin-top: 12px;
  }

  :global(.cs-chatkit-button) {
    align-items: center;
    border: 1px solid rgba(15, 23, 42, 0.16);
    border-radius: 8px;
    cursor: pointer;
    display: inline-flex;
    font: inherit;
    gap: 6px;
    justify-content: center;
    min-height: 34px;
    padding: 7px 12px;
    white-space: nowrap;
  }

  :global(.cs-chatkit-button-primary),
  :global(.cs-chatkit-button-solid) {
    background: #111827;
    border-color: #111827;
    color: #fff;
  }

  :global(.cs-chatkit-button-primary:hover:not(:disabled)),
  :global(.cs-chatkit-button-solid:hover:not(:disabled)) {
    background: #0b1220;
  }

  :global(.cs-chatkit-button-secondary) {
    background: rgba(15, 23, 42, 0.08);
    border-color: transparent;
    color: #172033;
  }

  :global(.cs-chatkit-button-secondary:hover:not(:disabled)) {
    background: rgba(15, 23, 42, 0.12);
  }

  :global(.cs-chatkit-button-outline),
  :global(.cs-chatkit-button-soft),
  :global(.cs-chatkit-button-ghost) {
    background: #fff;
    color: #172033;
  }

  :global(.cs-chatkit-button:disabled) {
    cursor: not-allowed;
    opacity: 0.55;
  }

  :global(.cs-chatkit-block) {
    width: 100%;
  }

  :global(.cs-chatkit-pill) {
    border-radius: 999px;
  }

  :global(.cs-chatkit-badge) {
    border-radius: 6px;
    display: inline-flex;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
    padding: 5px 7px;
  }

  :global(.cs-chatkit-badge-soft) {
    background: rgba(15, 23, 42, 0.08);
  }

  :global(.cs-chatkit-badge-solid) {
    background: #111827;
    color: #fff;
  }

  :global(.cs-chatkit-title) {
    font-weight: 650;
    line-height: 1.2;
  }

  :global(.cs-chatkit-title-sm) {
    font-size: 16px;
  }

  :global(.cs-chatkit-title-md) {
    font-size: 18px;
  }

  :global(.cs-chatkit-title-lg) {
    font-size: 22px;
  }

  :global(.cs-chatkit-title-xl),
  :global(.cs-chatkit-title-2xl),
  :global(.cs-chatkit-title-3xl),
  :global(.cs-chatkit-title-4xl),
  :global(.cs-chatkit-title-5xl) {
    font-size: 26px;
  }

  :global(.cs-chatkit-text),
  :global(.cs-chatkit-markdown) {
    line-height: 1.45;
  }

  :global(.cs-chatkit-text-sm) {
    font-size: 13px;
  }

  :global(.cs-chatkit-text-lg),
  :global(.cs-chatkit-text-xl) {
    font-size: 17px;
  }

  :global(.cs-chatkit-weight-medium) {
    font-weight: 500;
  }

  :global(.cs-chatkit-weight-semibold),
  :global(.cs-chatkit-weight-bold) {
    font-weight: 650;
  }

  :global(.cs-chatkit-truncate) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :global(.cs-chatkit-spacer) {
    flex: 1 1 auto;
    min-width: 8px;
  }

  :global(.cs-chatkit-image) {
    aspect-ratio: 1;
    background: rgba(15, 23, 42, 0.04);
    box-sizing: border-box;
    display: block;
    max-width: 100%;
    overflow: hidden;
  }

  :global(.cs-chatkit-image-flush) {
    border-radius: 0;
    width: 100%;
  }

  :global(.cs-chatkit-image-frame) {
    border: 1px solid rgba(15, 23, 42, 0.12);
  }

  :global(.cs-chatkit-image-placeholder) {
    align-items: center;
    background: linear-gradient(135deg, #f8fafc, #eef2f6);
    color: #94a3b8;
    display: flex;
    justify-content: center;
  }

  :global(.cs-chatkit-image-placeholder > .cs-chatkit-icon) {
    height: 28px;
    width: 28px;
  }

  :global(.cs-chatkit-icon) {
    align-items: center;
    color: currentColor;
    display: inline-flex;
    flex: 0 0 auto;
    height: 20px;
    justify-content: center;
    line-height: 1;
    width: 20px;
  }

  :global(.cs-chatkit-icon svg) {
    display: block;
    fill: none;
    height: 100%;
    stroke: currentColor;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-width: 2;
    width: 100%;
  }

  :global(.cs-chatkit-button-icon) {
    height: 16px;
    width: 16px;
  }

  :global(.cs-chatkit-icon-fallback) {
    font-size: 12px;
    line-height: 1;
    white-space: nowrap;
  }

  :global(.cs-chatkit-input) {
    background: #fff;
    border: 1px solid rgba(15, 23, 42, 0.18);
    border-radius: 8px;
    box-sizing: border-box;
    color: #172033;
    font: inherit;
    min-height: 36px;
    padding: 8px 10px;
    width: 100%;
  }

  :global(.cs-chatkit-divider) {
    background: rgba(15, 23, 42, 0.12);
    border: 0;
    width: 100%;
  }

  :global(.cs-chatkit-table) {
    border-collapse: collapse;
    font-size: 13px;
    width: 100%;
  }

  :global(.cs-chatkit-table th),
  :global(.cs-chatkit-table td) {
    border-bottom: 1px solid rgba(15, 23, 42, 0.1);
    padding: 8px;
    text-align: left;
  }

  :global(.cs-chatkit-chart) {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  :global(.cs-chatkit-chart-row) {
    display: flex;
    justify-content: space-between;
  }
</style>
